import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from './shared/StatusBadge';
import type { Invoice } from '@/types/payment';
import downloadIcon from '@/assets/images/svg/download_icon.svg';
import clsx from 'clsx';

interface InvoiceCardProps {
  invoice: Invoice;
  onDownload: (invoice: Invoice) => void;
}

export const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onDownload }) => {
  const labelClass = 'font-sans text-12 font-bold uppercase tracking-wider text-gray-500';

  return (
    <div
      className={clsx(
        'mb-4 flex w-full flex-col p-5',
        'rounded-20 border border-invoice-border',
        'bg-white/60',
        'shadow-sm'
      )}
    >
      {/* Top Row: Status & Date */}
      <div className='mb-4 flex items-center justify-between'>
        <div className='flex flex-col gap-1'>
          <span className={labelClass}>Status</span>
          <StatusBadge status={invoice.status} />
        </div>

        <div className='flex flex-col items-end gap-1'>
          <span className={labelClass}>Date</span>
          <span className='font-sans text-16 font-medium text-brand-800'>{invoice.date}</span>
        </div>
      </div>

      {/* Middle Section: Invoice, Description & Amount */}
      <div className='mb-6 space-y-3'>
        <div className='flex flex-col gap-1'>
          <span className={labelClass}>Invoice</span>
          <span className='font-sans text-18 font-bold text-brand-800 underline decoration-solid decoration-auto underline-offset-4'>
            {invoice.invoiceNumber}
          </span>
        </div>

        <div className='flex flex-col gap-1'>
          <span className={labelClass}>Description</span>
          <span className='font-sans text-16 font-normal leading-relaxed text-brand-800'>
            {invoice.description}
          </span>
        </div>

        <div className='flex flex-col gap-1'>
          <span className={labelClass}>Amount</span>
          <span className='font-sans text-18 font-bold text-brand-800'>
            {invoice.amount.toFixed(2)} {invoice.currency}
          </span>
        </div>
      </div>

      {/* Bottom: Action */}
      <div className='w-full'>
        <Button
          variant='payments-download-row'
          className='h-[44px] w-full justify-center gap-2 bg-white'
          onClick={() => onDownload(invoice)}
          disabled={!invoice.documentAvailable}
        >
          <img src={downloadIcon} alt='' className='size-4' />
          {invoice.documentAvailable ? 'Download' : 'Unavailable'}
        </Button>
      </div>
    </div>
  );
};
